import React, { useState } from 'react';
import classNames from 'classnames';
import Icon, { Size } from 'components/general/icon/icon';
import ConfirmationModal from 'components/general/confirmation_modal/ConfirmationModal';
import { getCSRFToken } from 'lib/request';

export type DocumentSectionProps = {
  id: string;
  name: string;
  uploadDate: string;
  previewUrl?: string;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  deleteUrl?: string;
  formName?: string;
  className?: string;
};

export default function DocumentSection({
  id,
  name,
  uploadDate,
  previewUrl,
  isSelected = false,
  onSelect,
  onDelete,
  deleteUrl,
  formName,
  className,
}: DocumentSectionProps) {
  const styles = require("./DocumentUploader.module.scss");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteUrl) {
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (onDelete && result.success) {
          onDelete(id);
        }
      }
    } else {
      onDelete?.(id);
    }
    setIsModalOpen(false);
  };

  const handleSelection = () => {
    onSelect?.(id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onSelect?.(id);
    }
  };

  const handlePreviewKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  const handleDeleteKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  const containerClassName = classNames(
    styles.documentWrapper,
    {
      [styles.selected]: isSelected,
    },
    className
  );

  return (
    <>
    <div
      role="button"
      tabIndex={0}
      className={containerClassName}
      onClick={handleSelection}
      onKeyDown={handleKeyDown}
    >
      {formName && (
        <input
          type="radio"
          name={formName}
          value={id}
          checked={isSelected}
          className={styles.hiddenRadioInput}
          aria-label={`Select ${name}`}
        />
      )}

      <div className={styles.content}>
        <Icon 
          name={isSelected ? "radioButtonChecked" : "radioButtonUnchecked"} 
          size={Size.MediumBig} 
          color="tuftsBlueBase"
        />

        <div className={`${styles.fileInfo} ${styles.hasFile}`}>
          <span className={styles.title}>
            {name}
          </span>
          <span className={styles.subtitle}>{uploadDate && `Uploaded on ${uploadDate}`}</span>
        </div>

        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.actionButton}
            aria-label="Preview document"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handlePreviewKeyDown}
          >
            <Icon name="removeRedEye" size={Size.MediumBig} color="black"/>
          </a>
        )}
        {(onDelete || deleteUrl) && (
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(e);
            }}
            onKeyDown={handleDeleteKeyDown}
            aria-label="Delete document"
          >
            <Icon name="deleteOutline" size={Size.MediumBig} color="black"/>
          </button>
        )}
      </div>
    </div>

    <ConfirmationModal
      isOpen={isModalOpen}
      title="Delete resume from profile?"
      description="Upon deleting, this resume will no longer be available on your profile."
      onConfirm={handleConfirmDelete}
      onClose={() => setIsModalOpen(false)}
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
    />
    </>
  );
}
